
import { profileService } from './profileService';
import { roleService } from './roleService';
import { supabase } from '@/lib/supabase';

/**
 * Unified service that combines profile and role services
 * With improved error isolation for auth process
 */
export const userProfileService = {
  /**
   * Fetch user profile data with error isolation
   */
  fetchUserProfile: async (userId: string) => {
    try {
      return await profileService.fetchUserProfile(userId);
    } catch (error) {
      console.error("[userProfileService] Error fetching profile:", error);
      // Return empty profile instead of propagating error
      return { 
        name: undefined,
        avatar_url: undefined,
        dealer_activation_status: 'NONE'
      };
    }
  },
  
  /**
   * Fetch user role information with error isolation
   */
  fetchUserRole: async (userId: string) => {
    try {
      console.log("[userProfileService] Fetching role for user:", userId);
      
      // Get role from role service
      const { roleId, roleName } = await roleService.fetchUserRole(userId);
      console.log("[userProfileService] Role fetched:", { roleId, roleName });
      
      return { roleId, roleName };
    } catch (error) {
      console.error("[userProfileService] Error fetching role:", error);
      console.log("[userProfileService] Falling back to USER role due to error");
      // Return default role instead of propagating error
      return { roleId: 'user', roleName: 'USER' };
    }
  },
  
  /**
   * Create user profile and default role with error isolation
   */
  createUserProfile: async (userId: string, email: string, name: string) => {
    // Create profile with error handling
    try {
      await profileService.createUserProfile(userId, email, name);
    } catch (profileError) {
      console.error("[userProfileService] Error creating profile:", profileError);
      // Continue without failing the entire operation
    }
    
    // Create default role with error handling
    try {
      await roleService.createUserRole(userId);
    } catch (roleError) {
      console.error("[userProfileService] Error creating role:", roleError);
      // Continue without failing the entire operation
    }
  },
  
  /**
   * Check dealer activation request status for a user with error isolation
   */
  checkDealerActivationStatus: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('dealer_activation_requests')
        .select('status')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (error) {
        console.error("[userProfileService] Error checking dealer activation status:", error);
        return 'NONE';
      }
      
      return data?.status || 'NONE';
    } catch (error) {
      console.error("[userProfileService] Exception while checking activation status:", error);
      return 'NONE';
    }
  }
};
